import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, Subject } from 'rxjs';
import { map, distinctUntilChanged, switchMap, filter, finalize, tap, catchError, take, debounceTime } from 'rxjs/operators';
import { AuthStore } from '@core/services/auth/auth.store';
import { PropertyService } from '@core/services/data/properties.service';
import { PropertyStore } from '@shared/services/property.store';
import { PropertyGroupService } from '@core/services/data/property-groups.service';
import { ContractsService } from '@core/services/data/contracts.service';
import { toObservable } from '@angular/core/rxjs-interop';
// import { LoansService } from '@core/services/data/loans.service';
import { TenantsService } from '@core/services/data/tenants.service';
import { GlobalIndexService } from '@core/services/indexation/global-index.service';
import { AppCoordinationService } from '@core/services/infra/app-coordination.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { coreStoresApplicable } from '@shared/definitions/workspace';

export interface LoadingState {
  properties: boolean;
  propertyGroups: boolean;
  contracts: boolean;
  loans: boolean;
  tenants: boolean;
  expenses: boolean;
  globalIndex: boolean;
}

export interface LoadingProgress {
  isLoading: boolean;
  message: string;
  progress: number;
  stage: string;
  completedServices: string[];
  totalServices: number;
}

/**
 * @domain Core/Services
 * 
 * Unified Loading Service - Coordinates all data loading at application startup
 * 
 * This service:
 * - Consolidates multiple loading services into one coordinated flow
 * - Prevents multiple loading states and flickering
 * - Provides unified progress tracking
 * - Manages service initialization order and dependencies
 * - Implements proper error handling and fallbacks
 * 
 * Replaces the fragmented approach of AppLoadingService, DataLoadingService, etc.
 */
@Injectable({
  providedIn: 'root'
})
export class UnifiedLoadingService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Loading state for each service
  private loadingStateSubject = new BehaviorSubject<LoadingState>({
    properties: false,
    propertyGroups: false,
    contracts: false,
    loans: false,
    tenants: false,
    expenses: false,
    globalIndex: false
  });

  // Progress tracking.
  //
  // `LoadingProgress.message` is English on purpose, NOT an i18n oversight: no
  // consumer reads it. `progress$` is subscribed by ProgressiveLoadingCoordinator
  // (`{ isLoading, progress }`) and by AuthenticatedLoadingCoordinator, whose
  // calculateAndUpdateProgress() only uses `completedServices`. Nothing renders
  // these strings — translate them only if a surface starts showing them.
  // Audit #38/#65.
  private progressSubject = new BehaviorSubject<LoadingProgress>({
    isLoading: false,
    message: 'Ready',
    progress: 0,
    stage: 'idle',
    completedServices: [],
    totalServices: 7
  });

  // Public observables
  public loadingState$ = this.loadingStateSubject.asObservable();
  public progress$ = this.progressSubject.asObservable();
  
  // Global loading state - true if any service is loading
  public isLoading$ = this.loadingState$.pipe(
    map(state => Object.values(state).some(loading => loading)),
    distinctUntilChanged(),
    debounceTime(50) // Small debounce to prevent rapid state changes
  );

  // All services loaded state — false while idle (before initializeApp),
  // true only after the load cycle reaches stage `complete`. Previously
  // every loading flag started `false`, so this emitted true at boot and
  // could race completion detection.
  public allServicesLoaded$ = this.progress$.pipe(
    map(progress => progress.stage === 'complete'),
    distinctUntilChanged()
  );

  private readonly authStore = inject(AuthStore);
  private readonly isAuthenticated$ = toObservable(this.authStore.isAuthenticated);
  private readonly workspaceStore = inject(WorkspaceStore);

  constructor(
    private propertyService: PropertyService,
    private propertyStore: PropertyStore,
    private propertyGroupService: PropertyGroupService,
    private contractsService: ContractsService,
    // private loansService: LoansService,
    private tenantsService: TenantsService,
    private globalIndexService: GlobalIndexService,
    private appCoordination: AppCoordinationService
  ) {
    // console.log('🚀 UnifiedLoadingService: Initialized');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the application - coordinate all service loading
   */
  public initializeApp(): Observable<boolean> {
    // console.log('🚀 UnifiedLoadingService: Starting application initialization...');
    
    // Start coordination
    this.appCoordination.startInitialization();
    
    this.updateProgress({
      isLoading: true,
      message: 'Initializing application...',
      progress: 0,
      stage: 'startup',
      completedServices: [],
      totalServices: 7
    });

    return this.isAuthenticated$.pipe(
      filter(isAuth => {
        // console.log('🔐 Authentication state:', isAuth);
        return isAuth === true;
      }),
      take(1),
      switchMap(() => {
        // console.log('✅ User authenticated, starting data loading...');
        return this.loadAllServices();
      }),
      finalize(() => {
        // console.log('🏁 UnifiedLoadingService: Initialization complete');
        this.appCoordination.completeInitialization();
        this.updateProgress({
          isLoading: false,
          message: 'Application ready',
          progress: 100,
          stage: 'complete',
      completedServices: ['properties', 'propertyGroups', 'contracts', 'loans', 'tenants', 'expenses', 'globalIndex'],
      totalServices: 7
        });
      })
    );
  }

  /**
   * Load all services in coordinated manner
   */
  private loadAllServices(): Observable<boolean> {
    // #441 lot 3: a tenant/service-provider workspace's OWN properties,
    // propertyGroups, contracts and tenants are permanently empty (their
    // portals read projections from their own workspace instead) — opening
    // these Firestore listeners there is three-plus round trips to nothing.
    // Skip them and mark the services loaded immediately.
    const needsCoreStores = coreStoresApplicable(this.workspaceStore.currentWorkspace()?.type ?? null);

    const serviceLoaders = [
      needsCoreStores ? this.loadProperties() : this.skipCoreStore('properties'),
      needsCoreStores ? this.loadPropertyGroups() : this.skipCoreStore('propertyGroups'),
      needsCoreStores ? this.loadContracts() : this.skipCoreStore('contracts'),
      this.loadLoans(),
      needsCoreStores ? this.loadTenants() : this.skipCoreStore('tenants'),
      this.loadExpenses(),
      this.loadGlobalIndex()
    ];

    // Load all services in parallel but track progress individually
    return combineLatest(serviceLoaders).pipe(
      map(results => results.every(result => result === true)),
      tap(success => {
        if (success) {
          // console.log('✅ All services loaded successfully');
          this.appCoordination.markServicesLoaded();
        } else {
          console.warn('⚠️ Some services failed to load');
        }
      })
    );
  }

  /**
   * #441 lot 3: stand-in for a core-store loader on a tenant/service-provider
   * workspace — marks the service (and, for 'properties', financialCalculations
   * too, same as loadProperties()' own empty-workspace branch, since there is
   * nothing to enrich either) without touching Firestore.
   */
  private skipCoreStore(serviceName: 'properties' | 'propertyGroups' | 'contracts' | 'tenants'): Observable<boolean> {
    this.appCoordination.markServiceLoaded(serviceName);
    if (serviceName === 'properties') {
      this.appCoordination.markServiceLoaded('financialCalculations');
    }
    return of(true);
  }

  /**
   * Load properties service
   */
  private loadProperties(): Observable<boolean> {
    return this.loadService('properties', 'Loading properties...', 15, () => {
      // Gate on the Firestore listener's first snapshot, NOT on the store
      // signal. `propertyStore.list` is seeded with `[]`, so a take(1) on it
      // resolved synchronously and marked 'properties' done before a single
      // document had arrived — which is what let the boot splash tear down
      // over an empty app. `hydrated$` flips only once the server answered
      // (an empty collection still counts, for brand-new workspaces).
      return this.propertyService.hydrated$.pipe(
        filter(hydrated => hydrated),
        take(1),
        map(() => {
          // The store bridge runs synchronously inside the same emission that
          // flips `hydrated$`, so the list is already populated here.
          const properties = this.propertyStore.list();
          // Mark service as loaded in coordination service
          this.appCoordination.markServiceLoaded('properties');
          // A brand-new/empty workspace has no properties to enrich, so the
          // PropertyListStateService enrichment stream may never run (and thus
          // never mark 'financialCalculations'). Mark it done here so the
          // authenticated loading screen doesn't stall at ~66% on an empty
          // workspace.
          if (properties.length === 0) {
            this.appCoordination.markServiceLoaded('financialCalculations');
          }
          return true;
        }),
        catchError(error => {
          console.error('❌ Error loading properties:', error);
          this.appCoordination.markServiceFailed('properties', error.message);
          return of(false);
        })
      );
    });
  }

  /**
   * Load property groups service
   */
  private loadPropertyGroups(): Observable<boolean> {
    return this.loadService('propertyGroups', 'Loading property groups...', 20, () => {
      return this.propertyGroupService.getPropertyGroupsByUser().pipe(
        // Wait for actual data, not just first emission
        filter(groups => groups !== null && groups !== undefined),
        take(1),
        map(_groups => {
          // Mark service as loaded in coordination service
          this.appCoordination.markServiceLoaded('propertyGroups');
          return true;
        }),
        catchError(error => {
          console.error('❌ Error loading property groups:', error);
          this.appCoordination.markServiceFailed('propertyGroups', error.message);
          return of(false);
        })
      );
    });
  }

  /**
   * Load contracts service
   */
  private loadContracts(): Observable<boolean> {
    return this.loadService('contracts', 'Loading rental contracts...', 30, () => {
      // Same hydration gate as loadProperties. This one ALSO marks the service
      // in AppCoordinationService, which it never did: `coreStoresHydrated$` in
      // AuthenticatedLoadingCoordinator requires properties && contracts &&
      // tenants, so with 'contracts' permanently false its `dataReady` branch
      // was dead and the splash could only ever end on the 2s timeout — always,
      // hot cache included.
      return this.contractsService.hydrated$.pipe(
        filter(hydrated => hydrated),
        take(1),
        map(() => {
          this.appCoordination.markServiceLoaded('contracts');
          return true;
        }),
        catchError(error => {
          console.error('❌ Error loading contracts:', error);
          this.appCoordination.markServiceFailed('contracts', error.message);
          return of(false);
        })
      );
    });
  }

  /**
   * Load loans service - temporarily disabled
   */
  private loadLoans(): Observable<boolean> {
    return this.loadService('loans', 'Loading financial data...', 45, () => {
      // Temporarily return success without loading loans
      // console.log('💰 Loans service temporarily disabled');
      return of(true);
    });
  }

  /**
   * Load tenants service
   */
  private loadTenants(): Observable<boolean> {
    return this.loadService('tenants', 'Loading tenants...', 60, () => {
      // Third leg of `coreStoresHydrated$` — see loadContracts.
      return this.tenantsService.hydrated$.pipe(
        filter(hydrated => hydrated),
        take(1),
        map(() => {
          this.appCoordination.markServiceLoaded('tenants');
          return true;
        }),
        catchError(error => {
          console.error('❌ Error loading tenants:', error);
          this.appCoordination.markServiceFailed('tenants', error.message);
          return of(false);
        })
      );
    });
  }

  /**
   * Load expenses service
   */
  private loadExpenses(): Observable<boolean> {
    return this.loadService('expenses', 'Loading expenses...', 75, () => {
      // Expenses load automatically via the EXPENSES_STORE's auth-gated
      // subscription; no action needed here — this hook now just confirms the
      // store is reachable, which it is by definition (providedIn: 'root').
      return of(true);
    });
  }

  /**
   * Load global index service
   */
  private loadGlobalIndex(): Observable<boolean> {
    return this.loadService('globalIndex', 'Loading global indices...', 90, () => {
      return this.globalIndexService.initialize().pipe(
        map(success => {
          console.log('🌍 Global index loaded:', success);
          return success;
        }),
        catchError(error => {
          console.error('❌ Error loading global index:', error);
          return of(false);
        })
      );
    });
  }

  /**
   * Generic service loader with progress tracking
   */
  private loadService(
    serviceName: keyof LoadingState,
    message: string,
    progress: number,
    loader: () => Observable<boolean>
  ): Observable<boolean> {
    // Set loading state for this service
    this.setServiceLoading(serviceName, true);
    
    // Update progress
    this.updateProgress({
      isLoading: true,
      message,
      progress,
      stage: serviceName,
      completedServices: this.getCompletedServices(),
      totalServices: 6
    });

    return loader().pipe(
      finalize(() => {
        // Set loading complete for this service
        this.setServiceLoading(serviceName, false);
        
        // Update completed services
        const completed = [...this.getCompletedServices(), serviceName];
        this.updateProgress({
          isLoading: completed.length < 6,
          message: completed.length < 6 ? message : 'Finalizing...',
          progress: Math.min(95, progress + 5),
          stage: serviceName,
          completedServices: completed,
          totalServices: 6
        });
      })
    );
  }

  /**
   * Set loading state for a specific service
   */
  private setServiceLoading(serviceName: keyof LoadingState, loading: boolean): void {
    const currentState = this.loadingStateSubject.value;
    this.loadingStateSubject.next({
      ...currentState,
      [serviceName]: loading
    });
  }

  /**
   * Update progress information
   */
  private updateProgress(progress: LoadingProgress): void {
    this.progressSubject.next(progress);
  }

  /**
   * Get list of completed services
   */
  private getCompletedServices(): string[] {
    const currentState = this.loadingStateSubject.value;
    return Object.entries(currentState)
      .filter(([_, loading]) => !loading)
      .map(([serviceName, _]) => serviceName);
  }

  /**
   * Force refresh all services
   */
  public refreshAllServices(): Observable<boolean> {
    console.log('🔄 UnifiedLoadingService: Refreshing all services...');
    return this.loadAllServices();
  }

  /**
   * Get current loading state
   */
  public getCurrentLoadingState(): LoadingState {
    return this.loadingStateSubject.value;
  }

  /**
   * Get current progress
   */
  public getCurrentProgress(): LoadingProgress {
    return this.progressSubject.value;
  }

  /**
   * Check if a specific service is loading
   */
  public isServiceLoading(serviceName: keyof LoadingState): Observable<boolean> {
    return this.loadingState$.pipe(
      map(state => state[serviceName]),
      distinctUntilChanged()
    );
  }

  /**
   * Wait for all services to be loaded
   */
  public waitForAllServices(): Observable<boolean> {
    return this.allServicesLoaded$.pipe(
      filter(allLoaded => allLoaded),
      take(1)
    );
  }
}
